'use client'

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { realtimeManager } from '@/lib/realtime'
import { safeRealtimeSubscribe, safeRealtimeCleanup } from '@/lib/realtime/safe-realtime'
import { customerKeys } from '@/lib/hooks/use-customers'
import { planRequestKeys } from '@/lib/hooks/use-plan-requests'
import { contractKeys } from '@/lib/hooks/use-contracts'

/**
 * リアルタイム購読を「失敗しても画面を落とさない」形で張る。
 *
 * なぜ必要か（2026-08-09 実測）:
 *   常時接続（WebSocket）を開けないとき、購読処理は同期例外を投げる。
 *   これらの購読は useEffect の中で行われるが、**useEffect 内の throw も
 *   ErrorBoundary まで伝播する**ため、認証済みアプリ全体が
 *   「エラーが発生しました」に差し替わり、案件一覧も検索も使えなくなっていた。
 *
 *   しかもこの落ち方は iPad(Safari/WebKit) 限定で出る。WebKit は WebSocket の生成が
 *   拒否されると同期例外を投げるのに対し、Chromium は非同期の error イベントにするため
 *   落ちない。「iPad の人だけ真っ白、PC の人は平気」という切り分けの難しい形になる。
 *
 *   自動更新は「あると便利」な機能であって、これが使えないことは
 *   業務を止める理由にならない。失うのは自動更新だけに閉じる。
 *
 * 回帰ガード: lib/utils/realtime/__tests__/use-realtime-resilience.test.tsx
 */
function safeSubscribe(label: string, subscribe: () => () => void): () => void {
  let unsubscribe: (() => void) | undefined
  safeRealtimeSubscribe(label, () => {
    unsubscribe = subscribe()
  })
  return () => safeRealtimeCleanup(label, () => unsubscribe?.())
}

/**
 * 顧客のリアルタイム更新を有効化
 * 他のユーザーによる変更を自動的にReact Queryキャッシュに反映
 */
export function useRealtimeCustomers() {
  const queryClient = useQueryClient()

  useEffect(() => {
    return safeSubscribe('customers', () =>
      realtimeManager.subscribeToCustomers((payload) => {
        switch (payload.eventType) {
          case 'INSERT':
            if (payload.new) {
              // 新規顧客をキャッシュに追加
              queryClient.setQueryData(customerKeys.detail(payload.new.id), payload.new)
              // リストを無効化して再取得
              queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
              queryClient.invalidateQueries({ queryKey: customerKeys.counts() })
            }
            break

          case 'UPDATE':
            if (payload.new) {
              // キャッシュを更新
              queryClient.setQueryData(customerKeys.detail(payload.new.id), payload.new)
              // パイプラインが変更された場合はリストも更新
              if (payload.old?.pipelineStatus !== payload.new.pipelineStatus) {
                queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
                queryClient.invalidateQueries({ queryKey: customerKeys.counts() })
              }
            }
            break

          case 'DELETE':
            if (payload.old?.id) {
              // キャッシュから削除
              queryClient.removeQueries({
                queryKey: customerKeys.detail(payload.old.id),
              })
              queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
              queryClient.invalidateQueries({ queryKey: customerKeys.counts() })
            }
            break
        }
      })
    )
  }, [queryClient])
}

/**
 * プラン依頼のリアルタイム更新を有効化
 */
export function useRealtimePlanRequests() {
  const queryClient = useQueryClient()

  useEffect(() => {
    return safeSubscribe('plan_requests', () =>
      realtimeManager.subscribeToPlanRequests((payload) => {
        switch (payload.eventType) {
          case 'INSERT':
            if (payload.new) {
              queryClient.setQueryData(planRequestKeys.detail(payload.new.id), payload.new)
              queryClient.invalidateQueries({ queryKey: planRequestKeys.lists() })
              queryClient.invalidateQueries({ queryKey: planRequestKeys.counts() })
            }
            break

          case 'UPDATE':
            if (payload.new) {
              queryClient.setQueryData(planRequestKeys.detail(payload.new.id), payload.new)
              queryClient.invalidateQueries({ queryKey: planRequestKeys.lists() })
              queryClient.invalidateQueries({ queryKey: planRequestKeys.counts() })
            }
            break

          case 'DELETE':
            if (payload.old?.id) {
              queryClient.removeQueries({
                queryKey: planRequestKeys.detail(payload.old.id),
              })
              queryClient.invalidateQueries({ queryKey: planRequestKeys.lists() })
              queryClient.invalidateQueries({ queryKey: planRequestKeys.counts() })
            }
            break
        }
      })
    )
  }, [queryClient])
}

/**
 * 契約のリアルタイム更新を有効化
 */
export function useRealtimeContracts() {
  const queryClient = useQueryClient()

  useEffect(() => {
    return safeSubscribe('contracts', () =>
      realtimeManager.subscribeToContracts((payload) => {
        switch (payload.eventType) {
          case 'INSERT':
            if (payload.new) {
              queryClient.setQueryData(contractKeys.detail(payload.new.id), payload.new)
              queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
            }
            break

          case 'UPDATE':
            if (payload.new) {
              queryClient.setQueryData(contractKeys.detail(payload.new.id), payload.new)
              queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
            }
            break

          case 'DELETE':
            if (payload.old?.id) {
              queryClient.removeQueries({
                queryKey: contractKeys.detail(payload.old.id),
              })
              queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
            }
            break
        }
      })
    )
  }, [queryClient])
}

/**
 * すべてのリアルタイム更新を有効化
 */
export function useRealtimeAll() {
  useRealtimeCustomers()
  useRealtimePlanRequests()
  useRealtimeContracts()
}
